/**
 * BoatInput.ts — keyboard controller for a Boat.
 *
 * W / ArrowUp    : gas + (throttle grip up)
 * S / ArrowDown : gas − (throttle grip down)
 * A / ArrowLeft  : steer left
 * D / ArrowRight : steer right
 * Q / E          : steer uncoupled outboard left / right
 *
 * The throttle model is split into independent gas (0..1) + gear (-1/0/+1)
 * to match how a real outboard / inboard control head behaves:
 *  - Keyboard W/S only moves gas. Changing gear is the player's call.
 *  - UI gear switcher sets gear without touching gas.
 *  - Combined drive `boat.throttle = boat.gear * boat.gas` is written each
 *    frame so the rest of the physics keeps using a single scalar.
 *
 * Gear changes leave gas untouched — switching F→N→R or directly F↔R keeps
 * the throttle grip exactly where the player left it.
 */

import type { Boat } from "./sim/Boat";

type Gear = -1 | 0 | 1;

export class BoatInput {
  private keys = new Set<string>();
  /** Target gas grip in [0, 1]. Smoothed into `boat.gas`. */
  private targetGas = 0;
  private gasOverride: number | null = null;
  private gearOverride: Gear | null = null;
  private steerOverride: number | null = null;
  private engineSteerOverride: number | null = null;

  constructor(
    private boat: Boat,
    target: Window | HTMLElement = window,
  ) {
    // capture:true ensures we receive keys BEFORE any focused UI element
    // (e.g. a dat.gui <select> dropdown) gets a chance to consume them.
    target.addEventListener("keydown", this.onDown as EventListener, {
      capture: true,
    });
    target.addEventListener("keyup", this.onUp as EventListener, {
      capture: true,
    });
  }

  private onDown = (e: KeyboardEvent): void => {
    const k = e.key.toLowerCase();
    this.keys.add(k);
    // Pressing a steering / throttle key takes control back from any GUI
    // override so the helmsman can always grab the keyboard wheel mid-drag.
    if (k === "w" || k === "s" || k === "arrowup" || k === "arrowdown") {
      this.gasOverride = null;
    }
    if (k === "a" || k === "d" || k === "arrowleft" || k === "arrowright") {
      this.steerOverride = null;
    }
    if (k === "q" || k === "e") {
      this.engineSteerOverride = null;
    }
    if (
      [
        "w",
        "a",
        "q",
        "s",
        "d",
        "e",
        "arrowup",
        "arrowdown",
        "arrowleft",
        "arrowright",
      ].includes(k)
    ) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  private onUp = (e: KeyboardEvent): void => {
    this.keys.delete(e.key.toLowerCase());
  };

  /**
   * Legacy combined throttle override. Kept for callers that still treat
   * the engine as a single signed lever (saves, AI scripts, tests). The
   * value is decomposed into gas and gear so the new physics path stays
   * consistent.
   */
  setThrottleOverride(value: number | null): void {
    if (value === null) {
      this.gasOverride = null;
      this.gearOverride = null;
      return;
    }
    const v = Math.max(-1, Math.min(1, value));
    if (v > 1e-3) {
      this.gearOverride = 1;
      this.gasOverride = v;
    } else if (v < -1e-3) {
      this.gearOverride = -1;
      this.gasOverride = -v;
    } else {
      this.gearOverride = 0;
      this.gasOverride = 0;
    }
  }

  /** Gas-only override in [0, 1]. Pass `null` to release back to keyboard. */
  setGasOverride(value: number | null): void {
    this.gasOverride = value === null ? null : Math.max(0, Math.min(1, value));
  }

  /** Gear override (-1, 0, +1). Pass `null` to release back to default. */
  setGearOverride(value: Gear | null): void {
    this.gearOverride = value;
  }

  setSteerOverride(value: number | null): void {
    this.steerOverride =
      value === null ? null : Math.max(-1, Math.min(1, value));
  }

  setEngineSteerOverride(value: number | null): void {
    this.engineSteerOverride =
      value === null ? null : Math.max(-1, Math.min(1, value));
  }

  step(dt: number): void {
    // Gas: incremental grip. W/S only moves the grip up/down; gear is
    // an independent control. In neutral the grip still moves (engine
    // revs without driving the prop), exactly like a real outboard.
    let tRate = 0;
    if (this.keys.has("w") || this.keys.has("arrowup")) tRate += 1;
    if (this.keys.has("s") || this.keys.has("arrowdown")) tRate -= 1;
    if (this.gasOverride === null) {
      // Idle → full grip in ~1s.
      const GAS_RATE = 1.0;
      this.targetGas = Math.max(
        0,
        Math.min(1, this.targetGas + tRate * GAS_RATE * dt),
      );
    } else {
      this.targetGas = this.gasOverride;
    }

    // Steering: incremental (helm-like). A/D drive the steering DEVICE
    // (not the boat heading). On a wheel, A turns the wheel left, which
    // also turns the bow left — so sign matches. On a tiller, A pushes
    // the tiller left, which turns the rudder right and swings the bow
    // RIGHT — so we invert the sign in tiller mode. Releasing keys leaves
    // the helm where it is (no auto-centering), like a real yacht.
    let sRate = 0;
    if (this.keys.has("a") || this.keys.has("arrowleft")) sRate -= 1;
    if (this.keys.has("d") || this.keys.has("arrowright")) sRate += 1;
    if (this.boat.params.steering === "tiller") sRate = -sRate;
    if (this.steerOverride === null) {
      // Keyboard helm: centre to full lock in ~1.8s, so held A/D inputs
      // turn the wheel deliberately instead of snapping to the stop.
      const STEER_RATE = 1.0 / 1.8;
      this.boat.steer = Math.max(
        -1,
        Math.min(1, this.boat.steer + sRate * STEER_RATE * dt),
      );
    } else {
      this.boat.steer = this.steerOverride;
    }

    if (
      this.boat.params.engineType === "outboard" &&
      !this.boat.params.engineSteeringCoupled
    ) {
      let eRate = 0;
      if (this.keys.has("q")) eRate -= 1;
      if (this.keys.has("e")) eRate += 1;
      if (this.engineSteerOverride === null) {
        const ENGINE_STEER_RATE = 1.0 / 1.8;
        this.boat.engineSteer = Math.max(
          -1,
          Math.min(1, this.boat.engineSteer + eRate * ENGINE_STEER_RATE * dt),
        );
      } else {
        this.boat.engineSteer = this.engineSteerOverride;
      }
    }

    // Apply gear if overridden. Without an override, gear stays put — there
    // is no keyboard gear toggle yet, so the default lives at whatever the
    // last UI / setter wrote (initial value: neutral).
    if (this.gearOverride !== null) {
      this.boat.gear = this.gearOverride;
    }

    // Gas smoothed (1st-order lag) toward the grip target.
    const lag = 1 - Math.exp(-dt / 0.2);
    this.boat.gas += (this.targetGas - this.boat.gas) * lag;
    // Combined drive signal — every consumer downstream still reads this.
    this.boat.throttle = this.boat.gear * this.boat.gas;
  }
}
